/* Who checked themselves in and is still standing there — the front desk's half
 * of the lobby tablet.
 *
 * It sits on the service bar beside *Next availability* and *Due now* because
 * it is the third answer to the same question those two are read for: who is in
 * front of me and what do I do with them. Next availability is what a walk-in
 * could have, Due now is who is about to arrive, and this is who arrived and
 * could not be dealt with. Somewhere else on the screen it would be a list the
 * desk has to remember to look at, and a waiting list nobody looks at is worse
 * than none — it collects the promise without keeping it.
 *
 * IN THE ORDER THEY CAME IN, and nothing on this component may reorder it. Not
 * by service, not by which one can be seated right now, not by how long the
 * work takes. The salon's answer to "why am I after her" has to be "she was
 * here first", and any other sort makes that untrue in a way the person in the
 * chair can see. The sort itself is `waitingRows` in js/rules/waitlist.js, with
 * a test that pins it.
 *
 * One tap seats somebody, and it is the same tap the bar's own service buttons
 * are: the board finds the technician. Two taps to remove — nobody standing in
 * a busy salon should be able to lose a client off the list by brushing it.
 */
import * as Vue from 'vue';
import { store } from '../store.js';

export const WaitingList = {
  name: 'WaitingList',
  setup: function () {
    // Which row has had its remove button uncovered. One at a time, and cleared
    // by anything else happening — see `seat` and the watcher below.
    var confirming = Vue.ref(null);

    function seat(entry) {
      confirming.value = null;
      store.seatCheckin(entry.id);
    }

    function askRemove(entry) {
      confirming.value = confirming.value === entry.id ? null : entry.id;
    }

    function remove(entry) {
      confirming.value = null;
      store.dropCheckin(entry.id);
    }

    /* The list changed under the open confirmation — another station seated
     * that person, or the desk did it from the other tablet. Fold it away
     * rather than leave a *Remove* button hanging over whoever moved up into
     * that row, which is the one press here that cannot be taken back. */
    Vue.watch(store.waitingCheckins, function (rows) {
      if (!confirming.value) return;
      var still = rows.some(function (r) { return r.id === confirming.value; });
      if (!still) confirming.value = null;
    });

    // How long they have been standing there, as a label. Ticks on the minute
    // with every other clock on the board.
    function waited(entry) {
      return store.waitLabel(store.checkinWaited(entry));
    }

    /* Past twenty minutes a row starts to look like one. Not a rule about
     * anything — nothing is refused and nothing changes — it is the list
     * raising its voice, because the failure mode of a waiting list is that it
     * is quietly correct while somebody sits in it for three quarters of an
     * hour. */
    function isLong(entry) {
      return store.checkinWaited(entry) >= 20;
    }

    return {
      rows: store.waitingCheckins,
      longest: store.longestWaitMinutes,
      confirming: confirming,
      seat: seat,
      askRemove: askRemove,
      remove: remove,
      waited: waited,
      isLong: isLong,
      serviceById: store.serviceById,
      noColor: store.defaultServiceColor
    };
  },
  template: [
    '<div class="waiting">',
    '  <span class="waiting__label">Waiting',
    '    <span class="waiting__n">{{ rows.length }}</span>',
    /* The head of the queue's wait, on the label, so the length of the list and
       the length of the wait are read in one glance. Four people who all arrived
       a minute ago and one person who has been there half an hour are very
       different afternoons and the count alone cannot tell them apart. */
    '    <span v-if="longest >= 5" class="waiting__longest">longest {{ longest }} min</span>',
    '  </span>',
    '  <ol class="waiting__row">',
    /* The key is the entry, not the position: keying by index would make
       somebody being seated look like every row below them changing service. */
    '    <li v-for="(w, i) in rows" :key="w.id" class="wait-chip"',
    '      :class="{ \'is-long\': isLong(w), \'is-confirming\': confirming === w.id }"',
    '      :style="{ \'--chip\': (serviceById[w.serviceId] || {}).color || noColor }">',
    /* The position, drawn rather than implied. The desk reads this list to a
       person out loud — "you are third" — and counting rows down a wrapped flex
       row by eye is exactly where that goes wrong. */
    '      <span class="wait-chip__pos" aria-hidden="true">{{ i + 1 }}</span>',
    '      <button type="button" class="wait-chip__main"',
    '        :title="w.clientName + \' — \' + w.serviceName + \', waiting \' + waited(w) + \'. Click to start their service.\'"',
    '        @click="seat(w)">',
    '        <span class="wait-chip__who">{{ w.clientName }}</span>',
    '        <span class="wait-chip__svc">{{ w.serviceName }}</span>',
    '      </button>',
    '      <span class="wait-chip__for">{{ waited(w) }}</span>',
    /* Two presses, and the second one says what it does. A single × here would
       drop somebody off a list they are still standing in front of, and the
       only way back is Settings-free guesswork about who it was. */
    '      <button v-if="confirming !== w.id" type="button" class="wait-chip__x"',
    '        :aria-label="\'Take \' + w.clientName + \' off the waiting list\'"',
    '        title="They left, or you dealt with them at the desk"',
    '        @click="askRemove(w)">&times;</button>',
    '      <button v-else type="button" class="wait-chip__drop"',
    '        @click="remove(w)">Remove</button>',
    '    </li>',
    '  </ol>',
    '</div>'
  ].join('\n')
};
